import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getRecentActivity, getDetailedExamResults, getSubjects } from '../../services/content';
import { getUserStreak, checkAndUpdateLearningStreak } from '../../services/auth';
import SubjectsList from '../content/SubjectsList';
import '../../styles/dashboard.css';
import '../../styles/dashboard-mobile.css';

const Dashboard = () => {
  const { profile, hasActiveSubscription, getSubscriptionStatus, getUserLevel, isSuperAdmin } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [detailedResults, setDetailedResults] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState({
    currentStreak: 0,
    longestStreak: 0,
    lastActiveDate: null,
    weeklyProgress: 0,
    weeklyGoal: 5
  });
  const [currentQuote, setCurrentQuote] = useState('');

  const motivationalQuotes = [
    "Success is the sum of small efforts repeated day in and day out.",
    "The expert in anything was once a beginner.",
    "Every accomplishment starts with the decision to try.",
    "Your limitation—it's only your imagination.",
    "Great things never come from comfort zones.",
    "Dream it. Wish it. Do it.",
    "Success doesn't just find you. You have to go out and get it.",
    "The harder you work, the luckier you get."
  ];

  const getRandomQuote = () => {
    return motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];
  };

  const checkAndUpdateStreak = async () => {
  if (!profile?.uid) return;
  
  try {
    console.log('🔥 Checking and updating learning streak...');
    const updatedStreak = await checkAndUpdateLearningStreak(profile.uid);
    console.log('📊 Updated streak data:', updatedStreak);
    setStreakData(updatedStreak);
  } catch (error) {
    console.error('❌ Error updating streak:', error);
    // Set default values on error
    setStreakData({
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: null,
      weeklyProgress: 0,
      weeklyGoal: 5
    });
  }
};

const loadUserStreak = async () => {
  if (!profile?.uid) return;
  
  try {
    console.log('📊 Loading user streak data...');
    const streakData = await getUserStreak(profile.uid);
    console.log('📈 Loaded streak data:', streakData);
    setStreakData(streakData);
  } catch (error) {
    console.error('❌ Error loading streak:', error);
    // Set default values on error
    setStreakData({
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: null,
      weeklyProgress: 0,
      weeklyGoal: 5
    });
  }
};

  const getBadges = () => {
    const badges = [];
    const { currentStreak, longestStreak, weeklyProgress, weeklyGoal } = streakData;
    
    if (currentStreak >= 1) badges.push({ id: 'first_day', name: 'First Day', icon: '🌟', earned: currentStreak >= 1 });
    if (currentStreak >= 3) badges.push({ id: 'three_days', name: '3-Day Streak', icon: '🔥', earned: currentStreak >= 3 });
    if (currentStreak >= 7) badges.push({ id: 'week_streak', name: 'Week Warrior', icon: '💪', earned: currentStreak >= 7 });
    if (currentStreak >= 14) badges.push({ id: 'two_weeks', name: 'Two Weeks', icon: '🏆', earned: currentStreak >= 14 });
    if (currentStreak >= 30) badges.push({ id: 'month_streak', name: 'Month Master', icon: '👑', earned: currentStreak >= 30 });
    if (longestStreak >= 10) badges.push({ id: 'consistent', name: 'Consistent', icon: '⭐', earned: longestStreak >= 10 });
    if (weeklyProgress >= weeklyGoal) badges.push({ id: 'weekly_goal', name: 'Goal Crusher', icon: '🎯', earned: weeklyProgress >= weeklyGoal });
    
    return badges;
  };

  const refreshDashboardData = async () => {
    if (hasActiveSubscription()) {
      try {
        setPerformanceLoading(true);
        
        const userLevel = getUserLevel();
        console.log('🔍 Refreshing dashboard data for user:', profile.uid, 'level:', userLevel);
        
        // Fetch subjects for proper name mapping
        const subjectsData = await getSubjects(userLevel);
        setSubjects(subjectsData);
        console.log('📚 Fetched subjects:', subjectsData);
        
        // Check and update learning streak
        checkAndUpdateStreak();
        
        // For super admin, don't fetch recent activity (no specific level)
        if (isSuperAdmin()) {
          setRecentActivity(null);
        } else {
          const activity = await getRecentActivity(profile.uid, userLevel);
          setRecentActivity(activity);
        }
        
        // Fetch detailed exam results for performance overview
        const results = await getDetailedExamResults(profile.uid, userLevel);
        console.log('📊 Fetched exam results:', results.length, 'results');
        console.log('📋 Results data:', results);
        setDetailedResults(results);
      } catch (error) {
        console.error('Error refreshing dashboard data:', error);
      } finally {
        setPerformanceLoading(false);
      }
    }
  };

  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (hasActiveSubscription()) {
        try {
          // Fetch subjects for proper name mapping
          const subjectsData = await getSubjects(getUserLevel());
          setSubjects(subjectsData);
          console.log('📚 Fetched subjects:', subjectsData);
          
          // Load existing streak data first
          await loadUserStreak();
          
          // Then check and update streak for today
          await checkAndUpdateStreak();
          
          // For super admin, don't fetch recent activity (no specific level)
          if (isSuperAdmin()) {
            setRecentActivity(null);
          } else {
            const activity = await getRecentActivity(profile.uid, getUserLevel());
            setRecentActivity(activity);
          }
          
          // Fetch detailed exam results for performance overview
          const results = await getDetailedExamResults(profile.uid, getUserLevel());
          setDetailedResults(results);
        } catch (error) {
          console.error('Error fetching recent activity:', error);
        } finally {
          setLoading(false);
          setPerformanceLoading(false);
        }
      } else {
        setLoading(false);
        setPerformanceLoading(false);
      }
    };

    fetchRecentActivity();
  }, [profile, hasActiveSubscription, getUserLevel, isSuperAdmin, checkAndUpdateStreak]);

  // Set motivational quote on component mount
  useEffect(() => {
    setCurrentQuote(getRandomQuote());
  }, []);

  // Refresh dashboard data when window gains focus (user returns from exam)
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 Dashboard window focused - refreshing data');
      refreshDashboardData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [profile, hasActiveSubscription, getUserLevel, isSuperAdmin, refreshDashboardData]);

  const getSubjectName = (subjectId) => {
    console.log('🔍 Looking up subject ID:', subjectId);
    
    // Find subject in the fetched subjects data
    const subject = subjects.find(s => s.id === subjectId);
    const result = subject ? subject.name : 'Unknown Subject';
    
    console.log('📝 Subject name result:', result);
    return result;
  };

  const getBestSubject = () => {
    if (detailedResults.length === 0) return 'N/A';
    
    const subjectScores = {};
    detailedResults.forEach(result => {
      if (!subjectScores[result.subjectId]) {
        subjectScores[result.subjectId] = [];
      }
      subjectScores[result.subjectId].push(result.percentage);
    });
    
    let bestSubject = '';
    let bestAverage = 0;
    
    Object.entries(subjectScores).forEach(([subject, scores]) => {
      const average = scores.reduce((acc, score) => acc + score, 0) / scores.length;
      if (average > bestAverage) {
        bestAverage = average;
        bestSubject = subject;
      }
    });
    
    return getSubjectName(bestSubject);
  };

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          {(() => {
            const displayName = profile?.firstName && profile?.lastName 
              ? `${profile.firstName} ${profile.lastName}`
              : profile?.email?.split('@')[0] || 'Student';
            console.log('🏠 Dashboard - Rendering with profile:', profile);
            console.log('🏠 Dashboard - Final displayName being shown:', displayName);
            console.log('🏠 Dashboard - Name source breakdown:', {
              'profile.firstName': profile?.firstName,
              'profile.lastName': profile?.lastName,
              'profile.displayName': profile?.displayName,
              'profile.email.split("@")[0]': profile?.email?.split('@')[0],
              'fallback': 'Student'
            });
            return displayName;
          })()}
          <h1>Welcome, {profile?.firstName && profile?.lastName 
            ? `${profile.firstName} ${profile.lastName}`
            : profile?.email?.split('@')[0] || 'Student'}!</h1>
          <p>Track your learning progress and access study materials</p>
        </div>

        <div className="dashboard-grid">
          {hasActiveSubscription() && (
            <>
              {/* Subjects Section */}
              <div className="dashboard-card full-width">
                <SubjectsList />
              </div>

              {/* Learning Streak Tracker */}
              <div className="dashboard-card full-width">
                <h3>Learning Streak Tracker</h3>
                <div className="streak-overview">
                  <div className="streak-stats">
                    <div className="streak-card current">
                      <div className="streak-icon">🔥</div>
                      <div className="streak-info">
                        <h4>Current Streak</h4>
                        <span className="streak-number">{streakData.currentStreak}</span>
                        <span className="streak-label">days</span>
                      </div>
                    </div>
                    
                    <div className="streak-card longest">
                      <div className="streak-icon">🏆</div>
                      <div className="streak-info">
                        <h4>Longest Streak</h4>
                        <span className="streak-number">{streakData.longestStreak}</span>
                        <span className="streak-label">days</span>
                      </div>
                    </div>
                    
                    <div className="streak-card weekly">
                      <div className="streak-icon">🎯</div>
                      <div className="streak-info">
                        <h4>Weekly Goal</h4>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${(streakData.weeklyProgress / streakData.weeklyGoal) * 100}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">
                          {streakData.weeklyProgress}/{streakData.weeklyGoal} days
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="achievements-section">
                    <h4>Achievement Badges</h4>
                    <div className="badges-grid">
                      {getBadges().map((badge) => (
                        <div key={badge.id} className={`badge ${badge.earned ? 'earned' : 'locked'}`}>
                          <div className="badge-icon">{badge.icon}</div>
                          <span className="badge-name">{badge.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="motivational-quote">
                    <div className="quote-icon">💭</div>
                    <p>"{currentQuote}"</p>
                  </div>
                </div>
              </div>

              {/* Performance Overview Section */}
              <div className="dashboard-card full-width">
                <div className="performance-overview-header">
                  <h3>Performance Overview</h3>
                  <button 
                    onClick={refreshDashboardData}
                    className="btn btn-secondary btn-sm"
                    disabled={performanceLoading}
                  >
                    {performanceLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
                {!performanceLoading && (
                  <>
                    {detailedResults.length > 0 ? (
                      <>
                        <div className="performance-summary">
                          <div className="summary-stats">
                            <div className="stat-card">
                              <h4>Average Score</h4>
                              <span className="stat-value">
                                {Math.round(detailedResults.reduce((acc, result) => acc + result.percentage, 0) / detailedResults.length)}%
                              </span>
                            </div>
                          <div className="stat-card">
                            <h4>Best Subject</h4>
                            <span className="stat-value">
                              {getBestSubject()}
                            </span>
                          </div>
                          <div className="stat-card">
                            <h4>Exams Taken</h4>
                            <span className="stat-value">{detailedResults.length}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="recent-exams">
                        <h4>Last 5 Exam Results</h4>
                        <div className="exam-results-grid">
                          {detailedResults.slice(0, 5).map((result, index) => (
                            <div key={result.id} className="exam-result-item">
                              <div className="exam-header">
                                <span className="exam-subject">{getSubjectName(result.subjectId)}</span>
                                <span className={`exam-score ${result.percentage >= 50 ? 'passed' : 'failed'}`}>
                                  {result.percentage}%
                                </span>
                              </div>
                              <div className="exam-details">
                                <span className="exam-date">{result.createdAt?.toDate?.()?.toLocaleDateString()}</span>
                                <span className="exam-status">
                                  {result.percentage >= 50 ? '✅ Passed' : '❌ Failed'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="no-performance-data">
                      <p>Start taking exams to get insights into your performance!</p>
                    </div>
                  )}
                  </>
                )}
              </div>

              {/* Recent Activity */}
              {!loading && recentActivity && (
                <div className="dashboard-card full-width">
                  <h3>Recent Activity</h3>
                  <div className="activity-grid">
                    <div className="activity-section">
                      <h4>Recent Exam Results</h4>
                      {recentActivity.recentResults.length > 0 ? (
                        <div className="recent-results">
                          {recentActivity.recentResults.map((result) => (
                            <div key={result.id} className="result-item">
                              <span className="result-score">{result.percentage}%</span>
                              <span className="result-status">
                                {result.passed ? '✅ Passed' : '❌ Failed'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No exam results yet. Start practicing!</p>
                      )}
                    </div>

                    <div className="activity-section">
                      <h4>New Past Papers</h4>
                      <div className="recent-papers">
                        {recentActivity.recentPapers.slice(0, 3).map((paper) => (
                          <div key={paper.id} className="paper-item">
                            <h5>{paper.title}</h5>
                            <p>{paper.year} - {paper.term}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
